import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import JsBarcode from "jsbarcode";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { getStatusAppearance } from "../../../config/statusColors";
import { formatInstantDateShortTr } from "../../../utils/appTime";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import Button from "../../../components/BaseUI/Button";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Label from "../../../components/BaseUI/Label";
import { FiCheckCircle, FiEye, FiArrowLeft } from "react-icons/fi";
import { IoMdBarcode } from "react-icons/io";

import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Pagination from "../../../components/BaseUI/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const PackagingProducts = () => {
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deviceToMark, setDeviceToMark] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [barcodeValue, setBarcodeValue] = useState(null);
  const barcodeRef = useRef(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState({
    sortBy: "serialNumber",
    sortOrder: "desc",
  });

  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/packaging-products",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  useTitle("Rovion MES - Paketleme");

  const splitAlphaNum = (s) => {
    const str = (s ?? "").toString();
    if (!str) return [];
    return str.match(/[A-Za-z]+|\\d+|[^A-Za-z\\d]+/g) || [];
  };

  // A -> Z, char-by-char, letters come before numbers
  const compareCharByCharAZ = (a, b) => {
    const ax = splitAlphaNum(a);
    const bx = splitAlphaNum(b);
    const n = Math.max(ax.length, bx.length);

    for (let i = 0; i < n; i++) {
      const as = ax[i] ?? "";
      const bs = bx[i] ?? "";
      if (!as && !bs) return 0;
      if (!as) return -1;
      if (!bs) return 1;

      const aIsDigits = /^\\d+$/.test(as);
      const bIsDigits = /^\\d+$/.test(bs);
      const aIsLetters = /^[A-Za-z]+$/.test(as);
      const bIsLetters = /^[A-Za-z]+$/.test(bs);

      const typeRank = (isLetters, isDigits) =>
        isLetters ? 0 : isDigits ? 1 : 2;
      const ar = typeRank(aIsLetters, aIsDigits);
      const br = typeRank(bIsLetters, bIsDigits);
      if (ar !== br) return ar - br;

      if (aIsDigits && bIsDigits) {
        const an = Number(as);
        const bn = Number(bs);
        if (an !== bn) return an - bn;
        if (as.length !== bs.length) return as.length - bs.length;
        if (as !== bs)
          return as.localeCompare(bs, "en", { sensitivity: "base" });
        continue;
      }

      const cmp = as.localeCompare(bs, "en", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
    }
    return 0;
  };

  const sortedModels = useMemo(() => {
    return [...(models || [])].sort((a, b) => {
      const byCode = compareCharByCharAZ(a?.code || "", b?.code || "");
      if (byCode !== 0) return byCode;
      return compareCharByCharAZ(a?.name || "", b?.name || "");
    });
  }, [models]);

  const modelColumns = [
    {
      name: "Model Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        compareCharByCharAZ(a?.name || "", b?.name || ""),
    },
    {
      name: "Model Kodu",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        compareCharByCharAZ(a?.code || "", b?.code || ""),
    },
    {
      name: "Ürün Sayısı",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => (a.deviceCount || 0) - (b.deviceCount || 0),
    },
    { name: "İşlemler", id: 3 },
  ];

  const deviceColumns = [
    { name: "Seri Numarası", dataType: "text", id: 0, sortKey: "serialNumber" },
    { name: "Durum", dataType: "text", id: 2, sortKey: "status" },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 3,
      sortKey: "createdAt",
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    { name: translate("UI.ACTIONS"), id: 4, sortable: false },
  ];

  const {
    visibleColumns: modelVisibleColumns,
    toggleColumnVisibility: toggleModelColumn,
    resetToDefault: resetModelColumns,
  } = useColumnVisibility("packaging-models-list", modelColumns);
  const {
    visibleColumns: deviceVisibleColumns,
    toggleColumnVisibility: toggleDeviceColumn,
    resetToDefault: resetDeviceColumns,
  } = useColumnVisibility("packaging-devices-list", deviceColumns);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Ürünler", url: "/products" },
    { label: "Paketleme" },
  ];

  useEffect(() => {
    fetchDeviceModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchDevices();
    }
  }, [
    pagination.page,
    pagination.limit,
    selectedModel,
    searchQuery,
    sort.sortBy,
    sort.sortOrder,
  ]);

  const fetchDevices = async () => {
    if (!selectedModel) return;

    try {
      setLoading(true);
      let url = `${API_URL}/api/devices?page=${pagination.page}&limit=${pagination.limit}&status=packaging&modelId=${selectedModel._id}`;

      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      url += `&sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setDevices(response.data.devices || []);
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || 1,
          limit: response.data.pagination.limit || 20,
          total: response.data.pagination.total || 0,
          totalPages: response.data.pagination.totalPages || 0,
          hasNextPage: response.data.pagination.hasNextPage || false,
          hasPrevPage: response.data.pagination.hasPrevPage || false,
        });
      }
    } catch (error) {
      console.error("Ürünler getirilemedi:", error);
      setToasts([
        {
          description: "Ürünler yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceModels = async () => {
    try {
      setModelsLoading(true);
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const activeModels = response.data.filter(
        (model) => model.status === "active",
      );

      // Fetch device counts for each model
      const modelsWithCounts = await Promise.all(
        activeModels.map(async (model) => {
          try {
            const devicesResponse = await axios.get(
              `${API_URL}/api/devices?status=packaging&modelId=${model._id}&limit=1`,
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );
            return {
              ...model,
              deviceCount: devicesResponse.data.pagination?.total || 0,
            };
          } catch (error) {
            return { ...model, deviceCount: 0 };
          }
        }),
      );

      setModels(modelsWithCounts);
    } catch (error) {
      console.error("Modeller getirilemedi:", error);
    } finally {
      setModelsLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      pending: "Beklemede",
      active: "Aktif",
      inactive: "Pasif",
      sold: "Satıldı",
      maintenance: "Bakımda",
      stock: "Stok",
      assembly: "Montaj",
      packaging: "Paketleme",
      delivered: "Teslim Edildi",
    };
    return statusLabels[status] || status;
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleMarkAsStock = async (device) => {
    setDeviceToMark({ device, action: "markStock" });
    setConfirmModalOpen(true);
  };

  const handleGenerateQR = async (device) => {
    try {
      setSelectedDevice(device);
      setQrModalOpen(true);
      setBarcodeValue(null);

      const response = await axios.get(
        `${API_URL}/api/serial-numbers/by-serial`,
        {
          params: {
            serialNumber: device.serialNumber,
          },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success && response.data.serialNumber?.serialNumber) {
        setBarcodeValue(response.data.serialNumber.serialNumber.toLowerCase());
      } else {
        setToasts([
          {
            description: "Barkod bulunamadı.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Barkod getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Barkod yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  useEffect(() => {
    if (barcodeValue && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, barcodeValue, {
          format: "CODE128",
          width: 2,
          height: 100,
          displayValue: true,
          fontSize: 16,
        });
      } catch (error) {
        console.error("Barkod oluşturma hatası:", error);
      }
    }
  }, [barcodeValue]);

  const confirmMarkAsStock = async () => {
    if (!deviceToMark || deviceToMark.action !== "markStock") return;

    try {
      await axios.put(
        `${API_URL}/api/devices/${deviceToMark.device._id}`,
        { status: "stock" },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setToasts([
        {
          description: "Ürün durumu başarıyla güncellendi.",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      fetchDevices();
      setConfirmModalOpen(false);
      setDeviceToMark(null);
    } catch (error) {
      console.error("Durum güncelleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.error || "Ürün durumu güncellenemedi.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      setConfirmModalOpen(false);
      setDeviceToMark(null);
    }
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBackToModels = () => {
    setSelectedModel(null);
    setDevices([]);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const totalProductsCount = models.reduce(
    (sum, model) => sum + (model.deviceCount || 0),
    0,
  );

  return (
    <AppPage
      title={selectedModel ? `${selectedModel.name} - Paketleme` : "Paketleme"}
      breadcrumbs={
        selectedModel
          ? [...breadcrumbs, { label: selectedModel.name }]
          : breadcrumbs
      }
      actions={
        selectedModel ? (
          <Button
            appearance="primary-subtle"
            onClick={handleBackToModels}
            icon={<FiArrowLeft />}
          >
            Modellere Dön
          </Button>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{
                padding: "8px 16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#6c757d",
                  fontWeight: "500",
                }}
              >
                Toplam Ürün:
              </span>
              <span
                style={{
                  fontSize: "16px",
                  color: "#00b4f3",
                  fontWeight: "700",
                }}
              >
                {totalProductsCount}
              </span>
            </div>
          </div>
        )
      }
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          {!selectedModel ? (
            <>
              <Table
                columns={modelColumns || []}
                visibleColumns={modelVisibleColumns}
                tableId="packaging-models-list"
                loading={modelsLoading}
                search={true}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={modelColumns}
                    visibleColumns={modelVisibleColumns}
                    onToggleColumn={toggleModelColumn}
                    onResetColumns={resetModelColumns}
                    showApply={false}
                    showReset={false}
                  />
                }
              >
                {sortedModels.map((model) => (
                  <Tr key={model._id} data={model}>
                    {modelVisibleColumns.includes(0) && <Td>{model.name}</Td>}
                    {modelVisibleColumns.includes(1) && <Td>{model.code}</Td>}
                    {modelVisibleColumns.includes(2) && (
                      <Td>
                        <span
                          style={{
                            backgroundColor: "#3B82F615",
                            color: "#3B82F6",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                            fontWeight: "600",
                          }}
                        >
                          {model.deviceCount || 0} Adet
                        </span>
                      </Td>
                    )}
                    {modelVisibleColumns.includes(3) && (
                      <Td>
                        <div className="d-flex gap-2">
                          <FiEye
                            className="cursor-pointer"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModelClick(model);
                            }}
                            title="Ürünleri Görüntüle"
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>
            </>
          ) : (
            <>
              <div className="mb-3">
                {/* <Button 
                  appearance="primary-subtle" 
                  onClick={handleBackToModels}
                  icon={<FiArrowLeft />}
                >
                  Modellere Dön
                </Button> */}
              </div>
              <Table
                columns={deviceColumns || []}
                visibleColumns={deviceVisibleColumns}
                tableId="packaging-devices-list"
                loading={loading}
                search={true}
                externalSearch={true}
                onSearch={(value) => {
                  setSearchQuery(value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                searchPlaceholder="Seri numarası ile ara..."
                externalSorting={true}
                onSort={(nextSort) => {
                  setSort(nextSort);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                hideFooter={true}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={deviceColumns}
                    visibleColumns={deviceVisibleColumns}
                    onToggleColumn={toggleDeviceColumn}
                    onResetColumns={resetDeviceColumns}
                    showApply={false}
                    showReset={false}
                  />
                }
              >
                {(devices || []).map((device) => (
                  <Tr key={device._id} data={device}>
                    {deviceVisibleColumns.includes(0) && (
                      <Td>{device.serialNumber}</Td>
                    )}
                    {deviceVisibleColumns.includes(2) && (
                      <Td>
                        <div
                          style={{
                            backgroundColor: getStatusAppearance(device.status)
                              .bg,
                            color: getStatusAppearance(device.status).text,
                            padding: "5px 10px",
                            borderRadius: "5px",
                            width: "fit-content",
                          }}
                        >
                          {getStatusLabel(device.status)}
                        </div>
                      </Td>
                    )}
                    {deviceVisibleColumns.includes(3) && (
                      <Td>{formatInstantDateShortTr(device.createdAt)}</Td>
                    )}
                    {deviceVisibleColumns.includes(4) && (
                      <Td>
                        <div className="d-flex gap-2">
                          <IoMdBarcode
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={() => handleGenerateQR(device)}
                            title="Barkod Göster"
                          />
                          {canWrite && (
                            <div
                              className="d-flex align-items-center gap-1"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleMarkAsStock(device)}
                              title="Paketleme → Stok: Ürünü stok aşamasına geçir"
                            >
                              <FiCheckCircle
                                style={{
                                  fontSize: "20px",
                                  color: "#14B8A6",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#14B8A6",
                                  fontWeight: "600",
                                }}
                              >
                                Stok
                              </span>
                            </div>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>

              {!loading && pagination.total > 0 && (
                <div className="mt-3">
                  <Pagination
                    totalItems={pagination.total}
                    totalPerPage={pagination.limit}
                    currentPage={pagination.page}
                    totalRecords={pagination.total}
                    onPageChange={({ pagination: paginationData }) => {
                      setPagination((prev) => ({
                        ...prev,
                        page: paginationData.currentPage,
                        limit: paginationData.itemsPerPage,
                      }));
                    }}
                    options={{
                      skipPrevious: true,
                      previous: true,
                      next: true,
                      skipNext: true,
                      selectQuantity: true,
                      simpleCounting: false,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeviceToMark(null);
        }}
        width="small"
      >
        <ModalHeader>Durumu Stok Olarak İşaretle</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu ürünün durumunu "Stok" olarak işaretlemek istediğinizden emin
            misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="primary" onClick={confirmMarkAsStock}>
              Onayla
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setConfirmModalOpen(false);
                setDeviceToMark(null);
              }}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={qrModalOpen}
        onClose={() => {
          setQrModalOpen(false);
          setSelectedDevice(null);
          setBarcodeValue(null);
        }}
        width="medium"
      >
        <ModalHeader>Barkod - {selectedDevice?.serialNumber}</ModalHeader>
        <ModalBody>
          <div style={{ textAlign: "center", padding: "20px" }}>
            {selectedDevice && (
              <>
                {barcodeValue ? (
                  <svg
                    ref={barcodeRef}
                    style={{ width: "100%", maxWidth: "750px" }}
                  ></svg>
                ) : (
                  <p>Barkod yükleniyor...</p>
                )}
                <p style={{ marginTop: "15px", fontWeight: "bold" }}>
                  Seri No: {selectedDevice.serialNumber}
                </p>
                {barcodeValue && (
                  <p
                    style={{
                      marginTop: "10px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Barkod: {barcodeValue}
                  </p>
                )}
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="subtle"
              onClick={() => {
                setQrModalOpen(false);
                setSelectedDevice(null);
                setBarcodeValue(null);
              }}
            >
              Kapat
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default PackagingProducts;
